import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, media } from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Check } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [variants, setVariants] = useState([]);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("overview");
  const [imgIdx, setImgIdx] = useState(0);
  const { addToCart, user } = useApp();

  useEffect(() => {
    setImgIdx(0);
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/variants/by-product/${slug}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { setVariants(d); if (d.length) setVariant(d[0]); })
      .catch(() => {});
  }, [slug]);

  const tabs = ["overview", "specifications", "research", "testing", "storage", "faq"];

  if (!product) return <div className="pt-40 text-center text-slate-500 font-mono text-sm">Loading product…</div>;

  const isCase = product.product_type === "case";
  const gallery = [...new Set([product.image, ...(product.gallery || [])].filter(Boolean))];
  const active = gallery[imgIdx] || gallery[0];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="glass rounded-3xl p-6 relative overflow-hidden" data-testid="product-media">
          {gallery.length > 0 ? (
            <>
              <div className="aspect-square rounded-2xl overflow-hidden bg-black/40 flex items-center justify-center">
                <img src={media(active)} alt={product.name} className="w-full h-full object-contain" data-testid="product-gallery-main" />
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 mt-4 flex-wrap" data-testid="product-gallery-thumbs">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setImgIdx(i)} data-testid={`product-gallery-thumb-${i}`}
                      className={`w-16 h-16 rounded-lg overflow-hidden border transition-all ${i === imgIdx ? "border-cyan-400/70 glow-cyan" : "border-white/10 hover:border-cyan-400/30"}`}>
                      <img src={media(g)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="min-h-[420px] flex flex-col items-center justify-center">
              <div className="scan-line" />
              <div className="vial-outer w-32 h-56 relative float-slow" data-testid="product-vial-visual">
                <div className="vial-cap" />
                <div className="vial-liquid" style={{ height: "55%" }} />
                <div className="absolute inset-x-0 bottom-4 text-center">
                  <div className="text-[10px] font-mono text-white/90">{product.name.replace("CG Labz ", "")}</div>
                  <div className="text-[9px] font-mono text-cyan-300">{variant ? `${variant.mg}${variant.unit}` : ""}</div>
                </div>
              </div>
            </div>
          )}
          <div className="mt-4 stat-pill mx-auto w-fit">CG Labz Verified</div>
        </div>

        <div>
          <div className="mono-label mb-3">{product.categories?.join(" · ")}</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white">
            {product.name}
            {isCase && (
              <span data-testid="case-badge" className="ml-3 align-middle px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-400/40 text-[10px] font-mono uppercase tracking-widest text-violet-200">Case</span>
            )}
          </h1>
          <p className="mt-3 text-slate-400 text-sm leading-relaxed">{product.tagline}</p>

          {variants.length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">{isCase ? "Color" : "Select Strength"}</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button key={v.id} onClick={() => setVariant(v)} data-testid={`variant-selector-${(v.label || v.mg).toString().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2.5 rounded-xl border text-sm font-mono transition-all ${variant?.id === v.id ? "border-cyan-400/70 bg-cyan-400/10 text-cyan-300 glow-cyan" : "border-white/10 bg-white/5 text-slate-300 hover:border-cyan-400/30"}`}>
                    {v.label || `${v.mg}${v.unit}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {variant && (
            <div className="mt-6 glass rounded-2xl p-5">
              <div className="flex items-end justify-between">
                <div>
                  <div className="flex items-baseline gap-3">
                    <div className="text-3xl font-mono text-white">${variant.price.toFixed(2)}</div>
                    {variant.compare_at_price > variant.price && (
                      <div className="text-sm font-mono text-slate-500 line-through">${variant.compare_at_price.toFixed(2)}</div>
                    )}
                  </div>
                  {user?.role === "wholesale" && <div className="text-xs font-mono text-emerald-400 mt-1">Wholesale: ${variant.wholesale_price?.toFixed(2)}</div>}
                </div>
                <div className="text-right">
                  {!isCase && variant.price_per_mg != null && <div className="text-xs font-mono text-cyan-300" data-testid="price-per-mg-display">${variant.price_per_mg}/mg</div>}
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{variant.inventory > 0 ? `${variant.inventory} in stock` : "Out of stock"}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex items-center gap-2 border border-white/10 rounded-xl px-2 py-1.5">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-6 text-slate-400">−</button>
                  <span className="w-6 text-center font-mono text-sm">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-6 text-slate-400">+</button>
                </div>
                <Button onClick={() => { addToCart(variant, product, qty); toast.success("Added to Lab Cart"); }} data-testid="add-to-cart-btn"
                  className="flex-1 btn-primary rounded-xl py-3 text-sm tracking-wide">
                  Add to Lab Cart
                </Button>
              </div>
              {!isCase && (
                <Link to={`/tools/calculator/${product.slug}`} data-testid="open-calculator-btn"
                  className="mt-3 flex items-center justify-center gap-2 text-xs font-mono text-cyan-300 hover:text-cyan-200">
                  Open Precision Calculator <ArrowRight size={12} />
                </Link>
              )}
            </div>
          )}

          {isCase && (
            <div className="mt-6 glass rounded-2xl p-5" data-testid="case-specifications">
              <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-4">Specifications</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[["Capacity", product.vial_capacity != null ? `${product.vial_capacity} vials${product.bac_water_slots ? ` + ${product.bac_water_slots} bac water` : ""}` : null],
                  ["Fits", product.fits_vials], ["Closure", product.closure],
                  ["Material", product.material], ["Temperature", product.temperature]]
                  .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="border-b border-white/5 pb-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{k}</div>
                    <div className="mt-1 text-sm text-slate-200">{v}</div>
                  </div>
                ))}
              </div>
              {product.features?.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Why it works</div>
                  <ul className="space-y-1.5">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-cyan-400 shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {product.perfect_for && (
                <div className="mt-5">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Perfect for</div>
                  <p className="text-sm text-slate-300 leading-relaxed">{product.perfect_for}</p>
                </div>
              )}
            </div>
          )}

          {product.bulk_tiers?.length > 0 && (
            <div className="mt-6 glass rounded-2xl p-5" data-testid="product-volume-pricing">
              <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-3">Volume Pricing</div>
              <div className="space-y-1.5">
                {product.bulk_tiers.map((t) => (
                  <div key={t.id || t.name} className="flex items-center justify-between text-sm font-mono text-slate-300">
                    <span>{t.name}</span>
                    <span className="text-slate-500">{t.min_qty}–{t.max_qty === 9999 ? "∞" : t.max_qty} units</span>
                    <span className="text-cyan-300">{t.discount_pct}% off</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-2 text-[11px] font-mono text-slate-500">
            <ShieldCheck size={13} className="text-cyan-400" /> {isCase ? "Pharmacist-designed · fridge & freezer safe." : "Every batch verified. COA available."}
          </div>
        </div>
      </div>

      <div className="mt-16">
        <div className="flex flex-wrap gap-1 border-b border-white/10 mb-6">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest rounded-t-xl transition-colors ${tab === t ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-500 hover:text-slate-300"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="glass rounded-2xl p-6 text-sm text-slate-300 leading-relaxed" data-testid="product-tab-content">
          {tab === "overview" && <p>{product.description}</p>}
          {tab === "specifications" && (
            isCase ? (
              <div className="grid sm:grid-cols-2 gap-3 font-mono text-xs">
                <div><span className="text-slate-500">Capacity</span><br />{product.vial_capacity} vials{product.bac_water_slots ? ` + ${product.bac_water_slots} bac water` : ""}</div>
                <div><span className="text-slate-500">Fits</span><br />{product.fits_vials}</div>
                <div><span className="text-slate-500">Closure</span><br />{product.closure}</div>
                <div><span className="text-slate-500">Material</span><br />{product.material}</div>
                <div><span className="text-slate-500">Temperature</span><br />{product.temperature}</div>
                <div><span className="text-slate-500">Compliance</span><br />{product.compliance_status}</div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3 font-mono text-xs">
                <div><span className="text-slate-500">SKU</span><br />{variant?.sku}</div>
                <div><span className="text-slate-500">Strength</span><br />{variant?.mg}{variant?.unit}</div>
                <div><span className="text-slate-500">Price / mg</span><br />${variant?.price_per_mg}</div>
                <div><span className="text-slate-500">Compliance</span><br />{product.compliance_status}</div>
              </div>
            )
          )}
          {tab === "research" && <p>{product.peptide?.overview || (isCase ? "A storage accessory for organizing your peptide protocol — not a peptide product." : "Research documentation available in the Peptide Library.")}</p>}
          {tab === "testing" && <p>{isCase ? "CG Labz cases are made from durable, impact-resistant, fridge- and freezer-safe materials designed for safe cold-chain storage." : "All CG Labz batches undergo third-party HPLC purity testing and mass spec identity confirmation. COAs are publicly accessible in the COA Library."}</p>}
          {tab === "storage" && <p>{isCase ? product.temperature : (product.peptide?.storage || "Lyophilized: -20°C. Reconstituted: 2–8°C.")}</p>}
          {tab === "faq" && <p>{isCase ? "A storage accessory only. Vials and peptides are sold separately." : "For research use only. Not for human consumption. Not medical advice."}</p>}
        </div>
      </div>
    </div>
  );
}
